import {NgTemplateOutlet} from '@angular/common';
import {Component, Input, OnInit, TemplateRef} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTooltip} from '@angular/material/tooltip';
import {Router} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {NavigationTableRequestModel, NavigationTableResponseItemModel, PermissionResponseModel} from '../../../models/file-table.model';
import {FolderTree} from '../../../models/file.model';
import {MemberModel} from '../../../models/user.model';
import {DocumentsApiService} from '../../../services/api/documents-api.service';
import {MemberService} from '../../../services/member.service';
import {AlertService} from '../../../services/notify/alert.service';
import {StatisticService} from '../../../services/statistic.service';
import {ComponentsModule} from '../../components.module';

type DocStatistic = {
    approver: number,
    sign: number
}

@Component({
    selector: 'app-documents-index-page',
    standalone: true,
    imports: [
        ComponentsModule,
        NgTemplateOutlet,
        MatTooltip,
        TranslatePipe,

    ],
    templateUrl: './documents-index-page.component.html',
    styleUrl: './documents-index-page.component.scss'
})
export class DocumentsIndexPageComponent implements OnInit {
    @Input() currentMember: MemberModel;

    mainTabs: 'all' | 'sign' | 'visa' = 'all';

    foldersTree: FolderTree[];
    collapseFolders = [];
    selectedFolder: FolderTree;
    openFolderInfo = false;

    collections: PermissionResponseModel[];
    selectedCollection: PermissionResponseModel;
    openCollectionInfo = false;
    collectionsPagination = new NavigationTableRequestModel();

    files: NavigationTableResponseItemModel[];
    selectedFile: NavigationTableResponseItemModel;
    filesPagination = new NavigationTableRequestModel();

    filesForSign: NavigationTableResponseItemModel[];
    signPagination: NavigationTableRequestModel = new NavigationTableRequestModel();
    selectedSignFile: NavigationTableResponseItemModel;

    filesForVisa: NavigationTableResponseItemModel[];
    visaPagination: NavigationTableRequestModel = new NavigationTableRequestModel();
    selectedVisaFile: NavigationTableResponseItemModel;

    fileKey: string;

    statisticData: DocStatistic;

    constructor(
        private documentsApi: DocumentsApiService,
        private alertService: AlertService,
        private router: Router,
        private memberService: MemberService,
        private dialog: MatDialog,
        private statistic: StatisticService,
    ) {
        this.signPagination.filter = 'inbox';
        this.signPagination.id = 0;
        this.signPagination.limit = 50;
        this.signPagination.current_page = 1;

        this.visaPagination.filter = 'approve';
        this.visaPagination.id = 0;
        this.visaPagination.limit = 50;
        this.visaPagination.current_page = 1;

        this.getStatistic();
    }

    ngOnInit(): void {
        this.getDocumentsTree();
    }

    getStatistic(): void {
        this.statistic.getIndexPageStatistics('docs').subscribe({
            next: res => {
                this.statisticData = res.docs as DocStatistic;
            }
        });
    }

    changeMainTab(tab: 'all' | 'sign' | 'visa') {
        this.mainTabs = tab;

        switch (tab) {
            case 'all':
                this.getDocumentsTree();
                break;

            case 'sign':
                this.getFilesForSign();
                break;

            case 'visa':
                this.getFilesForVisa();
                break;
        }
    }

    getDocumentsTree(): void {
        this.documentsApi.getFoldersTree(this.currentMember.guid).subscribe({
            next: res => {
                this.foldersTree = res.tree;
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    getCollections(folder: FolderTree): void {
        if (!this.selectedFolder || folder.folder.id !== this.selectedFolder.folder.id) {
            this.collectionsPagination.current_page = 1;
            this.selectedFolder = folder;
        }

        this.collectionsPagination.id = folder.folder.id;
        this.collectionsPagination.guid = this.currentMember.guid;
        this.files = undefined;
        this.selectedCollection = undefined;

        this.documentsApi.getFoldersAndFilesTable(this.collectionsPagination).subscribe({
            next: data => {
                this.collectionsPagination = data.pagination;
                this.collections = (data.data as PermissionResponseModel[]).filter(item => item.collection);
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    getFiles(collection: PermissionResponseModel): void {
        if (!this.selectedCollection || collection.collection.id !== this.selectedCollection.collection.id) {
            this.selectedCollection = collection;
            this.filesPagination.current_page = 1;
        }

        this.filesPagination.id = collection.collection.id;
        this.filesPagination.guid = this.currentMember.guid;
        this.filesPagination.filter = 'collection';

        this.documentsApi.getCollectionTable(this.filesPagination).subscribe({
            next: res => {
                this.selectedCollection = collection;
                this.filesPagination = res.pagination;

                this.files = res.data.map(x => {
                    return x.file;
                });
            }, error: () => {
                this.files = undefined;
            }
        });
    }

    selectFile(file: NavigationTableResponseItemModel): void {
        this.selectedFile = file;
    }

    getFilesForSign(): void {
        this.documentsApi.getFoldersAndFilesTable(this.signPagination).subscribe({
            next: res => {
                if (res.pagination.pages) {
                    this.signPagination = res.pagination;
                }
                delete this.signPagination.guid;

                this.filesForSign = res.data as NavigationTableResponseItemModel[];
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    getFileMembers(file: NavigationTableResponseItemModel, typeMembers: 'sign' | 'visa'): void {
        let membersGuids: string[];
        if (typeMembers === 'sign') {
            this.selectedSignFile = file;
            membersGuids = file.signers_guids.map(x => {
                return x.member_guid;
            });
        } else {
            this.selectedVisaFile = file;
            membersGuids = file.approvers_guids.map(x => {
                return x.member_guid;
            });
        }

        this.memberService.memberGuidsHandler(membersGuids).then(members => {
            if (typeMembers === 'sign') {
                this.selectedSignFile.signers_guids.forEach(guid => {
                    guid.user = members.find(member => member.guid === guid.member_guid);
                });
            } else {
                this.selectedVisaFile.approvers_guids.forEach(guid => {
                    guid.user = members.find(member => member.guid === guid.member_guid);
                });
            }
        });
    }

    getFilesForVisa(): void {
        this.documentsApi.getFoldersAndFilesTable(this.visaPagination).subscribe({
            next: res => {
                if (res.pagination.pages) {
                    this.visaPagination = res.pagination;
                }
                delete this.visaPagination.guid;

                this.filesForVisa = res.data as NavigationTableResponseItemModel[];
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    goTo(event: MouseEvent, file: NavigationTableResponseItemModel, modalTemplate: TemplateRef<any>): void {
        event.stopPropagation();
        // void this.router.navigate(['account/doc/nav/file', file.key]);

        this.fileKey = file.key;
        this.dialog.open(modalTemplate, {
            minWidth: '90vw',
            minHeight: '96vh',
            panelClass: 'doc-modal'
        });
    }
}
